import type { MouseEvent } from 'react';
import type { CasePhoto } from '@/domain';
import { useCasePhotoUrl } from '@/application/usecases/useKits';
import { casePhotoCaption, casePhotoItem } from '@/features/photos/photoViewerItems';
import { openPhotoTile } from '@/features/photos/photoViewerStore';
import { strings } from '@/shared/strings';
import { CameraIcon } from '@/ui/icons';
import styles from './CasePhotos.module.css';

/**
 * The case photographs, on every screen that shows a case. Section 6 of the
 * request of 16 September 2026.
 *
 * They were on the job view and the case record and nowhere else: not at Final
 * Check, not in the order drawer, and on a stage list not even a count. One
 * component now, extracted from the job view, which had the better of the two
 * implementations.
 *
 * The bytes come through the port, never an authenticated URL on an `<img>`: a
 * browser fetches an image without headers, which is what made every photograph
 * in the system a broken image the first time. While the bytes load, and if they
 * fail, the tile is a frame with its caption, which is the fact that matters.
 *
 * Proof-of-work photographs are a separate strip under a separate permission.
 * Everywhere in this section means the CASE photographs; the two are not merged.
 *
 * Section 6.3 of the request asks for this under `src/ui/data/`. It sits beside
 * `WorkProofStrip` instead, its sibling of exactly the same kind, because a
 * component that fetches bytes through a port belongs to `features`: `ui` may
 * not import `application`, and the lint rule enforcing that is the reason the
 * strip is here rather than there.
 */
export function CasePhotos({
  photos,
  jobOrderNo,
}: {
  photos: readonly CasePhoto[];
  /** The case, for the name of a photograph somebody downloads from the viewer. */
  jobOrderNo?: string;
}) {
  if (photos.length === 0) {
    return <p className={styles.none}>{strings.record.noPhotos}</p>;
  }

  const items = photos.map((photo) => casePhotoItem(photo));

  return (
    <div className={styles.strip} data-photo-tiles="">
      {photos.map((photo, index) => (
        <CasePhotoThumb
          key={photo.id}
          photo={photo}
          onOpen={(event) => openPhotoTile(event, items, index, jobOrderNo)}
        />
      ))}
    </div>
  );
}

/**
 * One tile: a button that opens the photograph in the image viewer, over the
 * screen it is on, never in a tab of its own. The request of 5 October 2026
 * (third), section 6.
 *
 * It was a link to the full file in a new tab, which on an installed iPhone
 * app leaves the app. The tile fetches only its thumbnail now; the full file
 * is the viewer's to fetch. A button from the start, bytes or no bytes: the
 * viewer shows the photograph as soon as it has it.
 */
function CasePhotoThumb({
  photo,
  onOpen,
}: {
  photo: CasePhoto;
  onOpen: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  const objectUrl = useCasePhotoUrl(photo.thumbnailUrl);
  const label = casePhotoCaption(photo);

  return (
    <button
      type="button"
      className={styles.photo}
      aria-label={strings.viewer.open(`${strings.record.photos} · ${label}`)}
      onClick={onOpen}
      data-photo-tile={photo.id}
    >
      {objectUrl === null ? (
        <span className={styles.blank} aria-hidden="true" data-photo-frame="">
          <CameraIcon />
        </span>
      ) : (
        <img src={objectUrl} alt="" draggable={false} data-photo-frame="" />
      )}
      <span className={styles.caption}>{label}</span>
    </button>
  );
}

/**
 * How many photographs a case has, for a row that has no room for a strip.
 * Nothing at all when there are none: a bench with no photographs on a case
 * should see no furniture about it.
 */
export function CasePhotoCount({ count }: { count: number }) {
  if (count === 0) return null;

  return (
    <span className={styles.count} title={strings.record.photos}>
      <CameraIcon />
      {count}
    </span>
  );
}
